import { useState, useEffect, useCallback, useRef } from 'react';

interface PreloaderState {
  isLoading: boolean;
  loadedImages: Set<string>;
  error: string | null;
}

/**
 * useImagePreloader - Story 6.5
 * Preloads current and adjacent images for smooth gallery navigation.
 *
 * @param photos - Array of photo URLs
 * @param currentIndex - Currently displayed photo index
 * @returns { isLoading, loadedImages, preloadImage }
 */
export function useImagePreloader(photos: string[], currentIndex: number): PreloaderState & {
  preloadImage: (src: string) => Promise<void>;
} {
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef<Set<string>>(new Set());

  // Preload a single image
  const preloadImage = useCallback((src: string): Promise<void> => {
    // Skip if already loaded or currently loading
    if (loadedImages.has(src) || loadingRef.current.has(src)) {
      return Promise.resolve();
    }

    loadingRef.current.add(src);

    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        loadingRef.current.delete(src);
        setLoadedImages((prev) => new Set([...prev, src]));
        setError(null);
        resolve();
      };

      img.onerror = () => {
        loadingRef.current.delete(src);
        setError(`Failed to load image: ${src}`);
        reject(new Error(`Failed to load image: ${src}`));
      };

      img.src = src;
    });
  }, [loadedImages]);

  // Preload current and adjacent images when index changes
  useEffect(() => {
    if (photos.length === 0) return;

    const imagesToPreload: string[] = [];

    // Current image (priority)
    if (photos[currentIndex]) {
      imagesToPreload.push(photos[currentIndex]);
    }

    // Previous image
    if (currentIndex > 0 && photos[currentIndex - 1]) {
      imagesToPreload.push(photos[currentIndex - 1]);
    }

    // Next image
    if (currentIndex < photos.length - 1 && photos[currentIndex + 1]) {
      imagesToPreload.push(photos[currentIndex + 1]);
    }

    // Preload all without blocking
    imagesToPreload.forEach((src) => {
      preloadImage(src).catch(() => {
        // Ignore errors - handled by error state
      });
    });
  }, [photos, currentIndex, preloadImage]);

  // Check if current image is loaded
  const currentIsLoading = photos[currentIndex]
    ? !loadedImages.has(photos[currentIndex]) && loadingRef.current.has(photos[currentIndex])
    : false;

  return {
    isLoading: currentIsLoading,
    loadedImages,
    error,
    preloadImage,
  };
}
